/**
 * MerchantPricing — Phase-1 public pricing page. Loop P1.
 *
 * Scope: content + presentation only. This page never triggers billing,
 * never touches wallets, never computes fees on-server. Fee examples are
 * rendered client-side from the two approved plan constants below.
 *
 * Contract:
 *   - Two plans only: Starter ($39.99/mo · 4.25% or $1.99) and Growth ($79.99/mo · 2.85% or $1.49)
 *   - Growth adds a $15/mo/province addon (info only — cannot be purchased here)
 *   - CTAs route to the existing /register?role=merchant flow. No new checkout.
 *   - All amounts CAD. Taxes shown as additional. Annual prices are the
 *     effective per-month rate; total upfront charge is spelled out.
 *   - Every fee/cashback line explicitly says "Billing activation coming soon".
 */
import { useMemo, useState } from "react";
import { DemoCta } from "@/components/demo/DemoCta";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { usePricing, cad as cadFmt } from "@/lib/pricing";
import { FinalPlanGrid, FinalAddons, FinalFeeExamples, FinalCashback, finalFaq } from "@/components/pricing/FinalPricing";
import {
  ArrowRight, Check, Info, ShieldCheck, Clock, Gift,
  MapPin, Store, Sparkles,
} from "lucide-react";

/* ────────────────────────────────────────────────────────────────
 * Approved plan constants — do not change without a written product
 * decision. These values are the single source of truth for the page.
 * ──────────────────────────────────────────────────────────────── */
const PLANS = {
  starter: {
    id: "starter",
    name: "Starter",
    tagline: "For merchants launching their first performance campaigns.",
    monthly: 39.99,
    annualPerMonth: 31.99,
    annualUpfront: 383.88,
    storefrontsIncluded: 1,
    provincesIncluded: 1,
    feePct: 4.25,
    feeMinimum: 1.99,
    features: [
      "1 merchant coupon storefront",
      "1 Canadian province per storefront",
      "Manual coupon creation",
      "Product-level offers",
      "Basic campaign & verified-sales reporting",
    ],
  },
  growth: {
    id: "growth",
    name: "Growth",
    tagline: "For brands scaling across regions with AI-assisted campaigns.",
    monthly: 79.99,
    annualPerMonth: 61.99,
    annualUpfront: 743.88,
    storefrontsIncluded: 3,
    provincesIncluded: 3,
    additionalProvinceMonthly: 11.99,
    additionalStorefrontMonthly: 19.99,
    feePct: 2.85,
    feeMinimum: 1.49,
    features: [
      "Everything in Starter",
      "3 storefronts included (add more for CA$19.99/mo each)",
      "Up to 3 Canadian provinces per storefront (add more for CA$11.99/mo each)",
      "Unlimited active offers",
      "AI-assisted coupon creation",
      "Advanced reporting",
      "Staff permissions & roles",
    ],
    recommended: true,
  },
};

/* Currency helpers — plain formatting, no decimal drift.
 * Fee math on this page is illustrative only; the authoritative fee engine
 * lives in the backend and is not touched by Loop P1. */
const cad = (n) => cadFmt(n);

const feeFor = (plan, sale) => {
  const pct = (sale * plan.feePct) / 100;
  return Math.max(pct, plan.feeMinimum);
};

/* ────────────────────────────────────────────────────────────────
 * Hero
 * ──────────────────────────────────────────────────────────────── */
function Hero() {
  return (
    <section className="relative z-10 max-w-6xl mx-auto px-6 pt-20 pb-16" data-testid="pricing-hero">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/60 backdrop-blur border border-cyan-200 text-[11px] uppercase tracking-widest text-cyan-800 font-semibold mb-6">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        Cyannect for merchants · Phase 1 · Online merchants only
      </div>
      <h1 className="font-display font-medium leading-[1.05] tracking-[-0.035em]" data-testid="pricing-hero-title">
        <div className="text-4xl md:text-5xl lg:text-6xl text-slate-900">Turn your offers into</div>
        <div className="text-4xl md:text-5xl lg:text-6xl pf-gradient-text gradient-shift mt-1 pb-2">
          measurable online sales.
        </div>
      </h1>
      <p className="mt-8 text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed">
        Keep your own website, your own checkout, and your own payment gateway. Cyannect brings
        the shoppers, tracks every attributed purchase, and only earns when you do — a Performance Fee that applies only when Cyannect
        delivers an eligible Verified Sale.
      </p>
      <div className="mt-4 text-sm text-slate-500">
        Cyannect does not process the customer's purchase payment. Every purchase completes on your
        website through your own payment provider.
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────
 * Billing toggle
 * ──────────────────────────────────────────────────────────────── */
function BillingToggle({ value, onChange, annualBadge = "Save ~22%" }) {
  return (
    <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white/70 backdrop-blur border border-slate-200 text-sm font-medium"
         data-testid="pricing-billing-toggle">
      {[
        ["monthly", "Monthly"],
        ["annual", "Annual"],
      ].map(([key, label]) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          data-testid={`pricing-billing-${key}`}
          className={`px-5 py-2 rounded-full transition-colors ${
            value === key
              ? "bg-slate-900 text-white shadow"
              : "text-slate-700 hover:text-slate-900"
          }`}
        >
          {label}
          {key === "annual" && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800">
              {annualBadge}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
 * Single plan card
 * ──────────────────────────────────────────────────────────────── */
function PlanCard({ plan, billing }) {
  const isAnnual = billing === "annual";
  const displayPerMonth = isAnnual ? plan.annualPerMonth : plan.monthly;

  return (
    <div
      data-testid={`pricing-card-${plan.id}`}
      className={`relative rounded-3xl p-8 flex flex-col ${
        plan.recommended
          ? "bg-white border-2 border-emerald-400 shadow-xl"
          : "bg-white/85 border border-slate-200"
      }`}
    >
      {plan.recommended && (
        <div className="absolute -top-3 left-8 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500 text-white text-[11px] font-semibold uppercase tracking-widest">
          <Sparkles className="w-3 h-3" /> Most popular
        </div>
      )}

      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
          plan.recommended ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"
        }`}>
          <Store className="w-5 h-5" />
        </div>
        <div>
          <div className="font-display text-2xl text-slate-900" data-testid={`pricing-card-${plan.id}-name`}>{plan.name}</div>
          <div className="text-sm text-slate-500">{plan.tagline}</div>
        </div>
      </div>

      <div className="mt-6 flex items-baseline gap-2">
        <div className="text-5xl font-display font-medium text-slate-900" data-testid={`pricing-card-${plan.id}-price`}>
          {cad(displayPerMonth)}
        </div>
        <div className="text-slate-500 text-sm">/ month</div>
      </div>
      <div className="mt-2 text-sm text-slate-500">
        {isAnnual ? (
          <>Billed annually at <strong className="text-slate-700">{cad(plan.annualUpfront)}</strong> upfront.</>
        ) : (
          <>Billed monthly. Taxes additional.</>
        )}
      </div>

      <div className="mt-6 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 p-4">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-semibold mb-1">
          Performance Fee
        </div>
        <div className="text-slate-900 dark:text-slate-100 font-medium">
          {plan.feePct}% of eligible sale <span className="text-slate-500 dark:text-slate-400">or</span>{" "}
          {cad(plan.feeMinimum)}, whichever is higher
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          Applied only to verified, attributed sales completed on your website.
        </div>
      </div>

      <ul className="mt-6 space-y-3 text-sm text-slate-700 flex-1" data-testid={`pricing-card-${plan.id}-features`}>
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-8 space-y-2">
        <Link
          to="/register?role=merchant"
          className={`w-full inline-flex items-center justify-center gap-2 py-3 rounded-full font-medium transition ${
            plan.recommended
              ? "bg-slate-900 text-white hover:bg-slate-800"
              : "bg-white border border-slate-300 text-slate-800 hover:border-slate-500"
          }`}
          data-testid={`pricing-card-${plan.id}-cta`}
        >
          Get started with {plan.name} <ArrowRight className="w-4 h-4" />
        </Link>
        <div className="text-center text-[11px] uppercase tracking-widest text-amber-700 font-semibold">
          Billing activation coming soon
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
 * Additional-province note
 * ──────────────────────────────────────────────────────────────── */
function AdditionalProvinceNote() {
  return (
    <div className="mt-8 max-w-3xl mx-auto rounded-2xl bg-white/80 backdrop-blur border border-slate-200 p-5 flex gap-4"
         data-testid="pricing-provinces-note">
      <MapPin className="w-5 h-5 text-cyan-700 shrink-0 mt-0.5" />
      <div className="text-sm text-slate-700 leading-relaxed">
        <strong className="text-slate-900">No merchant app required.</strong> If your customers
        purchase through your website, Cyannect can send qualified shoppers directly to your
        existing checkout. Already have an app? App destinations may also be configured where
        supported.
      </div>
      <div className="text-sm text-slate-700 leading-relaxed">
        <strong className="text-slate-900">Storefront &amp; province coverage.</strong> Starter includes
        1 storefront with 1 province; Growth includes 3 storefronts with up to 3 provinces per storefront.
        Each additional province is <strong className="text-slate-900">CA$11.99/month</strong> per
        storefront and each additional storefront is <strong className="text-slate-900">CA$19.99/month</strong>
        {" "}(bundles up to 3 provinces on either plan).
        Annual add-ons receive a flat <strong className="text-slate-900">15% discount</strong>. Province
        coverage does not limit how many store locations you can run.
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
 * How it works — 5 step flow
 * ──────────────────────────────────────────────────────────────── */
function HowItWorks() {
  const steps = [
    { n: "01", title: "You create an offer", body: "Publish a coupon or product-level offer from your Cyannect merchant portal in under two minutes." },
    { n: "02", title: "Shopper discovers it", body: "Cyannect surfaces your offer to shoppers browsing coupons, categories, and stores in our marketplace." },
    { n: "03", title: "Shopper pays on your site", body: "They complete checkout on your own website, using your own payment gateway. Cyannect never touches the transaction." },
    { n: "04", title: "Attribution verifies the sale", body: "Your approved Cyannect purchase-tracking integration reports the verified, attributed conversion." },
    { n: "05", title: "You are billed the Performance Fee", body: "Cyannect invoices only the applicable Performance Fee on verified sales — never a click, never an impression." },
  ];
  return (
    <section className="relative z-10 max-w-6xl mx-auto px-6 py-20" data-testid="pricing-how-it-works">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <div className="text-xs uppercase tracking-widest text-cyan-700 font-semibold mb-2">How Cyannect works</div>
        <h2 className="font-display text-3xl md:text-4xl text-slate-900">
          You keep the customer. We bring the sale.
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {steps.map((s) => (
          <div key={s.n} className="rounded-2xl bg-white/80 backdrop-blur border border-slate-200 p-5">
            <div className="text-emerald-600 font-display text-2xl">{s.n}</div>
            <div className="mt-2 font-medium text-slate-900">{s.title}</div>
            <div className="mt-2 text-sm text-slate-600 leading-relaxed">{s.body}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────
 * Fee example table
 * ──────────────────────────────────────────────────────────────── */
const FEE_EXAMPLE_AMOUNTS = [20, 50, 100, 200];

function FeeExamples() {
  const rows = useMemo(() => FEE_EXAMPLE_AMOUNTS.map((a) => ({
    sale: a,
    starter: feeFor(PLANS.starter, a),
    growth: feeFor(PLANS.growth, a),
  })), []);

  return (
    <section className="relative z-10 max-w-6xl mx-auto px-6 py-16" data-testid="pricing-fee-examples">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="text-xs uppercase tracking-widest text-cyan-700 font-semibold mb-2">Transparent fees</div>
        <h2 className="font-display text-3xl md:text-4xl text-slate-900">
          What you pay on a verified sale
        </h2>
        <p className="mt-4 text-slate-600">
          Illustrative only. Fee is calculated on <em>eligible sale value</em> — the amount paid after
          your merchant discount, excluding sales tax, shipping, tips, and gift-card purchases.
        </p>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/85 dark:bg-slate-900/40">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-300 uppercase tracking-widest text-xs">
            <tr>
              <th className="text-left px-6 py-4 font-semibold">Eligible sale value</th>
              <th className="text-right px-6 py-4 font-semibold">Starter fee (4.25% or CA$1.99)</th>
              <th className="text-right px-6 py-4 font-semibold">Growth fee (2.85% or CA$1.49)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
            {rows.map((r) => (
              <tr key={r.sale} data-testid={`pricing-fee-row-${r.sale}`}>
                <td className="px-6 py-4 text-slate-800 dark:text-slate-100 font-medium">{cad(r.sale)}</td>
                <td className="px-6 py-4 text-right text-slate-800 dark:text-slate-100 font-mono" data-testid={`fee-starter-${r.sale}`}>
                  {cad(r.starter)}
                </td>
                <td className="px-6 py-4 text-right text-slate-800 dark:text-slate-100 font-mono" data-testid={`fee-growth-${r.sale}`}>
                  {cad(r.growth)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────
 * 14-day conversion-referral refund policy
 * ──────────────────────────────────────────────────────────────── */
function RefundPolicy() {
  return (
    <section className="relative z-10 max-w-6xl mx-auto px-6 py-16" data-testid="pricing-refund-policy">
      <div className="grid md:grid-cols-2 gap-8 items-start">
        <div>
          <div className="text-xs uppercase tracking-widest text-cyan-700 font-semibold mb-2">Performance Fee refunds</div>
          <h2 className="font-display text-3xl md:text-4xl text-slate-900">A 14-day fee-adjustment window.</h2>
          <p className="mt-6 text-slate-600 leading-relaxed">
            Cyannect may reverse or adjust a Performance Fee for a verified cancellation,
            refund, return, or chargeback reported within <strong>14 calendar days</strong> of the
            customer's purchase.
          </p>
          <p className="mt-4 text-slate-600 leading-relaxed">
            After that 14-day period, the Performance Fee becomes final and is not
            refundable by Cyannect, even if you later refund the customer.
          </p>
        </div>
        <div className="rounded-2xl bg-white/85 border border-slate-200 p-6 space-y-4">
          {[
            { icon: Clock, title: "Timing", body: "The 14 calendar days start at the customer's original purchase timestamp." },
            { icon: ShieldCheck, title: "Only verified events", body: "A return being requested by the customer is not enough — we act on verified cancellations, refunds, or chargebacks." },
            { icon: Info, title: "Partial refunds", body: "Inside the window, the fee is recalculated on the remaining eligible sale value." },
            { icon: Info, title: "Your customer's rights are separate", body: "This deadline applies to the Cyannect fee only. It does not affect the customer's rights against your business under applicable law." },
          ].map((r) => (
            <div key={r.title} className="flex gap-3">
              <r.icon className="w-5 h-5 text-cyan-700 mt-0.5" />
              <div>
                <div className="font-medium text-slate-900">{r.title}</div>
                <div className="text-sm text-slate-600 leading-relaxed">{r.body}</div>
              </div>
            </div>
          ))}
          <div className="text-[11px] uppercase tracking-widest text-amber-700 font-semibold pt-2 border-t border-slate-100">
            Enforcement details subject to your merchant agreement and applicable law.
          </div>
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────
 * Customer cashback — 0.85% funded by Cyannect
 * ──────────────────────────────────────────────────────────────── */
function CashbackNotice() {
  return (
    <section className="relative z-10 max-w-6xl mx-auto px-6 py-16" data-testid="pricing-cashback">
      <div className="rounded-3xl bg-gradient-to-br from-cyan-50 to-emerald-50 border border-cyan-200 p-10 flex flex-col md:flex-row gap-8 items-start">
        <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center">
          <Gift className="w-7 h-7 text-emerald-700" />
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-cyan-800 font-semibold mb-2">Customer cashback</div>
          <h2 className="font-display text-3xl md:text-4xl text-slate-900">
            Cyannect funds shopper rewards — you do not.
          </h2>
          <p className="mt-4 text-slate-700 leading-relaxed max-w-3xl">
            Every verified, attributed purchase on your site earns the shopper{" "}
            <strong>0.85% cashback</strong>, calculated on the final eligible product amount after
            your discount. Cashback is <strong>funded by Cyannect</strong>, not by the merchant.
          </p>
          <ul className="mt-4 grid md:grid-cols-2 gap-x-8 gap-y-2 text-sm text-slate-700">
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5" /> Wallet starts in <strong>Pending</strong> state.</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5" /> Released to available balance after <strong>15 business days</strong>.</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5" /> Cancelled or recalculated on verified refunds & chargebacks.</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5" /> Business-day count uses the Canadian holiday calendar.</li>
          </ul>
          <div className="mt-5 text-[11px] uppercase tracking-widest text-amber-700 font-semibold">
            Cashback release automation is coming soon — no impact on your subscription or fees.
          </div>
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────
 * Eligibility — Phase 1 online merchants only
 * ──────────────────────────────────────────────────────────────── */
function Eligibility() {
  return (
    <section className="relative z-10 max-w-6xl mx-auto px-6 py-16" data-testid="pricing-eligibility">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="text-xs uppercase tracking-widest text-cyan-700 font-semibold mb-2">Who can join Phase 1</div>
        <h2 className="font-display text-3xl md:text-4xl text-slate-900">Built for online merchants first.</h2>
        <p className="mt-4 text-slate-600">
          Phase 1 is for merchants who already run a full ecommerce experience. You keep every
          part of your existing stack — Cyannect connects to it.
        </p>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {[
          { title: "Your own ecommerce website", body: "You own the storefront where the customer shops and checks out." },
          { title: "Your own payment gateway", body: "Stripe, Shopify Payments, Square, Moneris, PayPal, or another provider — you keep collecting the payment." },
          { title: "Compatible purchase tracking", body: "A supported Cyannect integration (e.g. our JS pixel, Shopify app, or server webhook) reports verified sales." },
        ].map((r) => (
          <div key={r.title} className="rounded-2xl bg-white/85 border border-slate-200 p-6">
            <div className="w-10 h-10 rounded-xl bg-cyan-50 text-cyan-700 flex items-center justify-center mb-4">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div className="font-medium text-slate-900">{r.title}</div>
            <div className="mt-2 text-sm text-slate-600 leading-relaxed">{r.body}</div>
          </div>
        ))}
      </div>
      <div className="mt-8 text-center text-sm text-slate-500">
        Offline stores, marketplaces without a checkout, and Cyannect-hosted checkout are on the Phase 2 roadmap.
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────
 * FAQ
 * ──────────────────────────────────────────────────────────────── */
function FAQ({ override }) {
  const items = override || [
    {
      q: "Who processes the customer's payment?",
      a: "You do. Cyannect does not touch the transaction. The customer completes checkout on your website through your existing payment gateway.",
    },
    {
      q: "What's the difference between the subscription and the Performance Fee?",
      a: "The subscription is your monthly (or annual) plan fee — it keeps your storefront, campaigns, and reporting live. The Performance Fee is charged only when Cyannect delivers an eligible Verified Sale — it's how we earn when your promotions work.",
    },
    {
      q: "How is province coverage priced?",
      a: "Starter includes 1 storefront with 1 province included. Growth includes 3 storefronts with up to 3 provinces per storefront. Each additional province is CA$11.99/month per storefront (15% off if billed annually). Each additional storefront is CA$19.99/month and bundles up to 3 provinces (same as Growth).",
    },
    {
      q: "Can I get a fee refunded if my customer returns something?",
      a: "Yes, within 14 calendar days of the customer's purchase and only on a verified event (a completed refund, return, cancellation, or chargeback). After 14 days the Performance Fee is final. Your customer's own rights against your business are separate and are set by applicable law.",
    },
    {
      q: "Who pays for the 0.85% shopper cashback?",
      a: "Cyannect funds it end-to-end. Merchants are not charged for cashback and do not administer it. Rewards move from Pending to Available after 15 business days, subject to verification.",
    },
    {
      q: "How does purchase tracking work?",
      a: "You install one of our approved integrations (JS pixel, Shopify app, or a signed server webhook) that reports a completed order. Only sales that flow through this integration and match a Cyannect referral are billed.",
    },
  ];
  return (
    <section className="relative z-10 max-w-4xl mx-auto px-6 py-20" data-testid="pricing-faq">
      <div className="text-center mb-10">
        <div className="text-xs uppercase tracking-widest text-cyan-700 font-semibold mb-2">Questions</div>
        <h2 className="font-display text-3xl md:text-4xl text-slate-900">The finer details.</h2>
      </div>
      <div className="space-y-3">
        {items.map((it, idx) => (
          <details
            key={idx}
            data-testid={`pricing-faq-item-${idx}`}
            className="group rounded-2xl bg-white/85 border border-slate-200 p-6 open:shadow-sm"
          >
            <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
              <span className="font-medium text-slate-900">{it.q}</span>
              <span className="text-cyan-700 transition-transform group-open:rotate-45">+</span>
            </summary>
            <p className="mt-4 text-slate-700 leading-relaxed">{it.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────
 * Final CTA
 * ──────────────────────────────────────────────────────────────── */
function FinalCTA({ final }) {
  return (
    <section className="relative z-10 max-w-4xl mx-auto px-6 py-20 text-center" data-testid="pricing-final-cta">
      <h2 className="font-display text-3xl md:text-4xl text-slate-900">
        Ready to turn your promotions into performance revenue?
      </h2>
      <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
        {final
          ? "Set up your Cyannect merchant account today and pick Basic, Starter or Pro — you can change plans any time."
          : "Set up your Cyannect merchant account today. Billing activation is coming soon — your onboarding, storefront, and offer creation are already live for approved beta merchants."}
      </p>
      <div className="mt-8 flex flex-wrap gap-3 justify-center">
        <Link
          to="/register?role=merchant"
          className="pf-btn-primary px-7 py-3 rounded-full inline-flex items-center gap-2"
          data-testid="pricing-final-cta-signup"
        >
          Start Now <ArrowRight className="w-4 h-4" />
        </Link>
        <DemoCta testid="pricing-schedule-demo" icon className="pf-btn-ghost px-7 py-3 rounded-full inline-flex items-center gap-2" />
      </div>
      <div className="mt-6 text-xs text-slate-500">
        All prices in CAD. Applicable taxes are additional.
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────
 * Page container
 * ──────────────────────────────────────────────────────────────── */
export default function Pricing() {
  const [billing, setBilling] = useState("monthly");
  const pricing = usePricing();
  const final = !!pricing?.final_active;

  return (
    <div className="min-h-[calc(100vh-4rem)] relative overflow-hidden" data-testid="merchant-pricing-page"
         data-pricing-version={pricing?.pricing_version || "loading"}>
      <Seo
        title={final ? "Merchant pricing — Basic, Starter and Pro plans" : "Merchant pricing — Starter and Growth plans"}
        description={final
          ? "Cyannect merchant plans in CAD: Basic with no monthly fee, Starter and Pro with lower performance fees. Pay a small fee only on verified sales."
          : "Cyannect merchant plans in CAD. Starter CA$39.99/mo covers 1 storefront in 1 province at 4.25% or CA$1.99 per verified sale. Growth CA$79.99/mo covers 3 storefronts with up to 3 provinces each at 2.85% or CA$1.49 per verified sale. Add-ons: CA$19.99/mo per extra storefront (bundles up to 3 provinces), CA$11.99/mo per extra province (15% off annual)."}
        path="/pricing"
        keywords={final ? "Cyannect merchant pricing, Basic plan, Starter plan, Pro plan, merchant subscription CAD"
          : "Cyannect merchant pricing, Canadian coupon platform pricing, Starter plan, Growth plan, merchant subscription CAD"}
      />
      {/* Ambient backdrop matching the merchant landing page — mobile uses smaller
          blob sizes so decorative layers never trigger horizontal overflow probes. */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-70">
        <div className="blob blob-cyan blob-float w-[320px] h-[320px] md:w-[720px] md:h-[720px] -top-20 -right-20 md:-top-40 md:-right-40" />
        <div className="blob blob-mint blob-float w-[280px] h-[280px] md:w-[560px] md:h-[560px] top-1/3 -left-20 md:-left-40" style={{ animationDelay: "3s" }} />
      </div>

      <Hero />

      {/* Plan cards + billing toggle */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 pb-8">
        <div className="flex justify-center mb-10">
          <BillingToggle value={billing} onChange={setBilling} annualBadge={final ? "2 months free" : undefined} />
        </div>
        {pricing === undefined ? (
          <div className="h-[520px] rounded-3xl bg-white/50 border border-slate-200 animate-pulse" data-testid="pricing-plans-loading" />
        ) : final ? (
          <FinalPlanGrid pricing={pricing} billing={billing} />
        ) : (
          <div className="grid md:grid-cols-2 gap-6 items-stretch">
            <PlanCard plan={PLANS.starter} billing={billing} />
            <PlanCard plan={PLANS.growth} billing={billing} />
          </div>
        )}
        {pricing !== undefined && !final && <AdditionalProvinceNote />}
      </section>

      {final && <FinalAddons pricing={pricing} />}
      <HowItWorks />
      {final ? <FinalFeeExamples pricing={pricing} /> : <FeeExamples />}
      <RefundPolicy />
      {final ? <FinalCashback pricing={pricing} /> : <CashbackNotice />}
      <Eligibility />
      <FAQ override={final ? finalFaq(pricing) : null} />
      <FinalCTA final={final} />
    </div>
  );
}
